// Search-state for the closed-hours index route. The route in AppRouter.tsx owns
// the validateSearch schema and stripSearchParams middleware; this hook is a
// thin, typed surface over TanStack's useSearch / useNavigate so consumers don't
// touch the router primitives directly. Switching tab resets sort so the per-tab
// default applies.

// External
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

export type ClosedHoursTab = 'upcoming' | 'past';

export interface ClosedHoursSearchParams {
  tab: ClosedHoursTab;
  q: string;
  page: number;
  perPage: number;
  sort: string;
  direction: 'asc' | 'desc' | '';
}

export const CLOSED_HOURS_SEARCH_DEFAULTS: ClosedHoursSearchParams = {
  tab: 'upcoming',
  q: '',
  page: 1,
  perPage: 50,
  sort: '',
  direction: '',
};

const CLOSED_HOURS_ROUTE = '/closed_hours' as const;

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parseClosedHoursSearch(
  raw: Record<string, unknown>,
): ClosedHoursSearchParams {
  const tab: ClosedHoursTab = raw.tab === 'past' ? 'past' : 'upcoming';
  const direction: ClosedHoursSearchParams['direction'] =
    raw.direction === 'asc' || raw.direction === 'desc' ? raw.direction : '';
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  return {
    tab,
    q: typeof raw.q === 'string' ? raw.q : CLOSED_HOURS_SEARCH_DEFAULTS.q,
    page: Number.isFinite(page) && page > 0 ? page : CLOSED_HOURS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0
        ? perPage
        : CLOSED_HOURS_SEARCH_DEFAULTS.perPage,
    sort: typeof raw.sort === 'string' ? raw.sort : CLOSED_HOURS_SEARCH_DEFAULTS.sort,
    direction,
  };
}

export function useClosedHoursRouterSearch() {
  const search = useSearch({ from: CLOSED_HOURS_ROUTE });
  const tanNavigate = useNavigate({ from: CLOSED_HOURS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<ClosedHoursSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      // Switching tab resets sort so the per-tab default kicks in.
      setTab: (tab: ClosedHoursTab) => navigate({ tab, page: 1, sort: '', direction: '' }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      // Empty sort always pairs with empty direction; a non-empty sort with an
      // empty direction defaults to ascending so the URL is well-formed.
      setSort: (sort: string, direction: 'asc' | 'desc' | '') =>
        navigate({
          sort,
          direction: sort ? direction || 'asc' : '',
          page: 1,
        }),
    }),
    [search, navigate],
  );
}
